import type { IFunctionGetMetric } from '~/modules/analytics/types';
import type { IAnalyticMetric } from '~/types/metric';

import {
  getAddToCartRateMetric,
  getAverageOrderValueMetric,
  getAverageTimeOnPageMetric,
  getConversionRateMetric,
  getRevenuePerVisitorMetric,
  getTotalCollectionViews,
  getTotalATCMetric,
  getTotalPageViewMetric,
  getTotalProductViewMetric,
  getTotalRevenueMetric,
  getTotalSessionReachedCheckoutMetric,
  getTotalVisitorMetric,
  getTotalSessionMetric,
  getTotalOrderMetric,
  getBounceRateMetric,
  getLinkCtrMetric,
  getDefaultMetric,
  getTotalSessionWithCartAdditionMetric,
  getTotalSessionCompletedCheckoutMetric,
} from '~/modules/analytics';

export interface ICampaignAnalyticMetricFunc<T = IAnalyticMetric> {
  /**
   * @title Bounce rate
   * @content Percentage of visitors who leave your store after viewing only one page, without interacting further.
   * @formula sessions with a pageview / total sessions
   */
  getBounceRateMetric: IFunctionGetMetric<T>;
  /**
   * @title Add to cart rate
   * @content Percentage of sessions in which the customer added item to the cart
   * @formula sessions with cart additions / total sessions
   */
  getATCRateMetric: IFunctionGetMetric<T>;
  /**
   * @title Conversion rate
   * @content Percentage of online store sessions that completed an order
   * @formula sessions that completed checkout / total sessions
   */
  getConversionRateMetric: IFunctionGetMetric<T>;
  /**
   * @title Revenue
   * @content Total income generated from sales after discounts
   * @formula gross sales - discounts
   */
  getTotalRevenueMetric: IFunctionGetMetric<T>;
  /**
   * @title Visitors
   * @content Number of unique individuals who visit your online store
   */
  getTotalVisitorMetric: IFunctionGetMetric<T>;
  /**
   * @title Sessions
   * @content A period during which a visitor interacts with your online store
   */
  getTotalSessionMetric: IFunctionGetMetric<T>;
  /**
   * @title Pageviews
   * @content Number of times a page on your online store has been viewed by visitors
   */
  getTotalCollectionViews: IFunctionGetMetric<T>;
  /**
   * @title Pageviews
   * @content Number of times a page on your online store has been viewed by visitors
   */
  getTotalProductViewMetric: IFunctionGetMetric<T>;
  /**
   * @title Add to cart
   * @content Total number of the event when a customer adds a product to their cart on your online store
   */
  getTotalATCMetric: IFunctionGetMetric<T>;
  /**
   * @title Sessions that reached checkout
   * @content Sessions in which the checkout page was reached
   */
  getTotalCheckoutStartedMetric: IFunctionGetMetric<T>;
  /**
   * @title Average order value
   * @content Average value of orders placed in your online store
   * @formula total revenue / total orders
   */
  getAovMetric: IFunctionGetMetric<T>;
  /**
   * @title Revenue per visitor
   * @content Average revenue generated from each visitor
   * @formula total revenue / total visitors
   */
  getRpvMetric: IFunctionGetMetric<T>;
  /**
   * @title Orders
   * @content Number of orders that went through tested pages in this experiment
   */
  getTotalOrderMetric: IFunctionGetMetric<T>;
  /**
   * @title Average time on page
   * @content Average duration that visitors spend on a page before clicking to open new page
   * @formula total time on page / (total pageviews - total exits)
   */
  getAvgTimeOnPageMetric: IFunctionGetMetric<T>;
  /**
   * @title Pageviews
   * @content Number of times a page on your online store has been viewed by visitors
   */
  getTotalViewMetric: IFunctionGetMetric<T>;
  /**
   * @title Click-through rate
   * @content Percentage of clicks to open new page, compared to the number of times the page was viewed
   * @formula total clicks / total pageviews
   */
  getLinkCtrMetric: IFunctionGetMetric<T>;
  /**
   * @title Default metric
   * @content Default metric for analysis
   */
  getDefaultMetric: IFunctionGetMetric<T>;
  /**
   * @title Sessions with cart additions
   * @content Sessions in your online store in which a visitor added item to the cart
   */
  getTotalSessionWithCartAdditionMetric: IFunctionGetMetric<T>;
  /**
   * @title Sessions that reached checkout
   * @content Sessions in your online store in which the checkout page was reached
   */
  getTotalSessionReachedCheckoutMetric: IFunctionGetMetric<T>;
  /**
   * @title Sessions that completed checkout
   * @content Sessions in your online store in which a purchase was completed
   */
  getTotalSessionCompletedCheckoutMetric: IFunctionGetMetric<T>;
}

export const useCampaignAnalyticMetricFunc = (): ICampaignAnalyticMetricFunc => {
  return {
    getConversionRateMetric: getConversionRateMetric,
    getTotalRevenueMetric: getTotalRevenueMetric,
    getTotalVisitorMetric: getTotalVisitorMetric,
    getTotalSessionMetric: getTotalSessionMetric,
    getTotalCollectionViews: getTotalCollectionViews,
    getTotalProductViewMetric: getTotalProductViewMetric,
    getTotalATCMetric: getTotalATCMetric,
    getTotalCheckoutStartedMetric: getTotalSessionReachedCheckoutMetric,
    getATCRateMetric: getAddToCartRateMetric,
    getAovMetric: getAverageOrderValueMetric,
    getRpvMetric: getRevenuePerVisitorMetric,
    getTotalOrderMetric: getTotalOrderMetric,
    getAvgTimeOnPageMetric: getAverageTimeOnPageMetric,
    getTotalViewMetric: getTotalPageViewMetric,
    getBounceRateMetric: getBounceRateMetric,
    getLinkCtrMetric: getLinkCtrMetric,
    getDefaultMetric: getDefaultMetric,
    getTotalSessionWithCartAdditionMetric: getTotalSessionWithCartAdditionMetric,
    getTotalSessionReachedCheckoutMetric: getTotalSessionReachedCheckoutMetric,
    getTotalSessionCompletedCheckoutMetric: getTotalSessionCompletedCheckoutMetric,
  };
};
